import { z } from "zod";
import { Email, IsoDateTime, NonEmptyString, partialWithoutDefaults, ShortText, Uuid } from "./common.js";

/** Roles are additive: Químico can do everything Recepción can, Administrador everything. */
export const Role = z.enum(["administrador", "quimico", "recepcion"]);
export type Role = z.infer<typeof Role>;

/** Display names, es-MX. The role code never reaches the screen. */
export const RoleLabel: Record<Role, string> = {
  administrador: "Administrador",
  quimico: "Químico",
  recepcion: "Recepción",
};

export const Tenant = z.object({
  id: Uuid,
  name: NonEmptyString,
  slug: z.string().regex(/^[a-z0-9-]{2,40}$/),
});
export type Tenant = z.infer<typeof Tenant>;

/**
 * Who signs a branch's reports (migration 0013, NOM-007 5.1.8): each study's validator, or the
 * branch's responsable sanitario on every report ("o vigilar que sean firmados por el personal
 * … por él autorizado" — both are lawful; the validator always stays in the record).
 */
export const ReportSigner = z.enum(["validator", "responsible"]);
export type ReportSigner = z.infer<typeof ReportSigner>;

export const ReportSignerLabel: Record<ReportSigner, string> = {
  validator: "Quien valida cada estudio",
  responsible: "La persona responsable sanitaria",
};

export const Branch = z.object({
  id: Uuid,
  name: NonEmptyString,
  short_code: z.string().regex(/^[A-Z0-9]{1,4}$/),
  address: ShortText.nullable(),
  phone: ShortText.nullable(),
  responsible_name: ShortText.nullable(),
  responsible_cedula: ShortText.nullable(),
  /** The laboratory user who is the responsable sanitario (her signature prints in `responsible` mode). */
  responsible_user_id: Uuid.nullable(),
  report_signer: ReportSigner,
  /** The price list Reception and Quotes use here by default (migration 0018); null = the tenant's default list. */
  price_list_id: Uuid.nullable(),
  is_active: z.boolean(),
});
export type Branch = z.infer<typeof Branch>;

/** What any logged-in user may see about a user — never the password hash. */
export const User = z.object({
  id: Uuid,
  email: Email,
  full_name: NonEmptyString,
  role: Role,
  can_validate: z.boolean(),
  can_close_cash: z.boolean(),
  cedula: ShortText.nullable(),
  default_branch_id: Uuid.nullable(),
  is_active: z.boolean(),
});
export type User = z.infer<typeof User>;

// ---- Auth ------------------------------------------------------------------------------

export const LoginRequest = z.object({
  email: Email,
  password: z.string().min(1).max(256),
});
export type LoginRequest = z.infer<typeof LoginRequest>;

/**
 * Present only when the session belongs to a platform operator (liveratti support, ADR 0017).
 * `user` then describes the operator as an Administrador-shaped principal inside the current
 * tenant (no validation, no cash close), and `tenants` lists every laboratory it may enter.
 */
/**
 * Whether the current laboratory's staff may sign in on the platform host (`app.probetalab.com`),
 * an operator-only switch, off by default (ADR 0025, proposal §8.3). Shown to operators only.
 */
export const PlatformLoginState = z.object({
  enabled: z.boolean(),
  changed_at: IsoDateTime.nullable(),
  reason: z.string().nullable(),
  /** The operator who last changed it; null for the migration's backfill. */
  changed_by: z.string().nullable(),
  /** The host the switch is about, e.g. `app.probetalab.com`. */
  platform_host: NonEmptyString,
});
export type PlatformLoginState = z.infer<typeof PlatformLoginState>;

/** POST /v1/operator/platform-login — the switch; the reason is mandatory and goes to the lab's chain. */
export const PlatformLoginSet = z.object({
  enabled: z.boolean(),
  reason: z.string().trim().min(3, "Escribe el motivo.").max(500),
});
export type PlatformLoginSet = z.infer<typeof PlatformLoginSet>;

export const OperatorSummary = z.object({
  id: Uuid,
  email: Email,
  full_name: NonEmptyString,
  tenants: z.array(Tenant),
  /** The current laboratory's platform-host switch (ADR 0025). */
  platform_login: PlatformLoginState,
  /**
   * True on a laboratory's own host: the session is bound to that laboratory and the operator
   * cannot switch to another one there (that is what app.probetalab.com is for).
   */
  host_bound: z.boolean(),
});
export type OperatorSummary = z.infer<typeof OperatorSummary>;

/**
 * Laboratory-level interface settings (tenant `settings` jsonb, written only through the
 * `tenant.settings.set` command by an Administrador; migration 0014). An absent key takes its default (LAB_SETTINGS_DEFAULTS).
 */
export const LabSettings = z.object({
  /** Wide screens open the staff sidebar as an icon rail; each user may still expand it on their device. */
  compact_sidebar: z.boolean(),
  /**
   * Reception registers every new tube as already collected (by the registering user, at
   * registration time) unless the receptionist marks it pending; off = every tube is born
   * pending until someone taps «Muestra tomada» (migration 0017, pilot feedback 2026-11-02).
   */
  samples_collected_at_registration: z.boolean(),
  /**
   * How many days a new quote stays valid (migration 0025, pilot report 2026-11-03: Sonder quotes
   * for a month). Stored on each quote as `valid_until` when it is created, so changing it never
   * moves quotes already given out. Default 15.
   */
  quote_validity_days: z.number().int().min(1).max(365),
  /**
   * Capture grid colours (migration 0026, pilot request 2026-11-03): on = a value below the
   * interval is red and one above it orange; off (default) = both are orange. Display only: the
   * stored colour and the printed report do not change.
   */
  capture_low_red: z.boolean(),
  /**
   * The result report's "*" mark on a text result that differs from its expected text (POSITIVO
   * where NEGATIVO is normal) and the legend line that explains it (migration 0035, pilot request
   * 2026-11-03). Off = neither prints; Alto (*A) and Bajo (*B) always do. Default on.
   */
  report_text_flag: z.boolean(),
});
export type LabSettings = z.infer<typeof LabSettings>;
export const LAB_SETTINGS_DEFAULTS: LabSettings = { compact_sidebar: false, samples_collected_at_registration: false, quote_validity_days: 15, capture_low_red: false, report_text_flag: true };

/** `tenant.settings.set` — the laboratory's settings (Administrador). Send only the keys that change. */
export const TenantSettingsSetPayload = z
  .object({
    compact_sidebar: z.boolean().optional(),
    samples_collected_at_registration: z.boolean().optional(),
    quote_validity_days: z.number().int().min(1).max(365).optional(),
    capture_low_red: z.boolean().optional(),
    report_text_flag: z.boolean().optional(),
  })
  .strict()
  .refine((p) => Object.values(p).some((v) => v !== undefined), { message: "Indica al menos una preferencia." });
export type TenantSettingsSetPayload = z.infer<typeof TenantSettingsSetPayload>;
export const TenantSettingsSetResult = z.object({ lab_settings: LabSettings });
export type TenantSettingsSetResult = z.infer<typeof TenantSettingsSetResult>;

export const LoginResponse = z.object({
  user: User,
  tenant: Tenant,
  /** The laboratory's interface settings, defaults applied. */
  lab_settings: LabSettings,
  /**
   * The name the laboratory shows (tenant setting `display_name`, else `tenant.name`). The staff
   * app titles its browser tab with it once signed in.
   */
  lab_display_name: NonEmptyString,
  /** True when the laboratory has a valid logo; its bytes are at `GET /v1/lab/logo` (the tab's favicon). */
  has_logo: z.boolean(),
  /**
   * The laboratory's preferred origin (ADR 0025): the host we advertise, e.g.
   * `https://sistema.laboratoriosonder.com` or `https://sonder.probetalab.com`. The device
   * remembers it so the platform host can point the lab's staff there.
   */
  preferred_origin: z.string().url(),
  branches: z.array(Branch),
  session_expires_at: IsoDateTime,
  operator: OperatorSummary.optional(),
});
export type LoginResponse = z.infer<typeof LoginResponse>;

/** POST /v1/operator/tenant — the operator enters another laboratory; the response is the new session view. */
export const OperatorTenantSelect = z.object({ tenant_id: Uuid });
export type OperatorTenantSelect = z.infer<typeof OperatorTenantSelect>;

/** POST /v1/auth/set-password — one-time link (operator onboarding today, user resets later). */
export const SetPasswordRequest = z.object({
  token: z.string().min(20).max(200),
  password: z.string().min(10).max(256),
});
export type SetPasswordRequest = z.infer<typeof SetPasswordRequest>;

export const SetPasswordResponse = z.object({ ok: z.literal(true) });
export type SetPasswordResponse = z.infer<typeof SetPasswordResponse>;

/** GET /v1/me */
export const MeResponse = LoginResponse;
export type MeResponse = z.infer<typeof MeResponse>;

/**
 * GET /v1/public/branding — who this host belongs to, before anyone signs in (ADR 0025).
 * On a laboratory's host: its display name, whether `GET /v1/public/logo` has a logo, and its
 * origin. On the platform host: `lab` is null.
 */
export const PublicBranding = z.object({
  host_kind: z.enum(["platform", "tenant"]),
  lab: z
    .object({
      name: NonEmptyString,
      has_logo: z.boolean(),
      /** This host's origin, e.g. `https://sonder.probetalab.com`. */
      origin: z.string().url(),
      /**
       * The laboratory's preferred origin — the one we advertise and put in links, e.g.
       * `https://sistema.laboratoriosonder.com` once its own domain is preferred. Every verified
       * host serves the app; this is the canonical one the device remembers.
       */
      preferred_origin: z.string().url(),
    })
    .nullable(),
  /** The platform host of this deployment, e.g. `app.probetalab.com`. */
  platform_host: NonEmptyString,
});
export type PublicBranding = z.infer<typeof PublicBranding>;

/** GET /v1/public/manifest.webmanifest — the installed app's name and icon, per host. */
export const WebManifest = z.object({
  name: NonEmptyString,
  short_name: NonEmptyString,
  description: z.string(),
  lang: z.string(),
  start_url: z.string(),
  scope: z.string(),
  id: z.string(),
  display: z.literal("standalone"),
  orientation: z.string(),
  background_color: z.string(),
  theme_color: z.string(),
  icons: z.array(z.object({ src: z.string(), sizes: z.string(), type: z.string(), purpose: z.string() })),
});
export type WebManifest = z.infer<typeof WebManifest>;

/** GET /v1/tls/ask?domain= — Caddy's on-demand TLS permission check (loopback only). */
export const TlsAskQuery = z.object({ domain: z.string().max(300) });
export const TlsAskResponse = z.object({ ok: z.literal(true) });

export const HealthResponse = z.object({
  status: z.literal("ok"),
  db: z.literal("ok"),
  version: z.string(),
  time: IsoDateTime,
});
export type HealthResponse = z.infer<typeof HealthResponse>;

// ---- User administration (Administrador) ------------------------------------------------

export const UserCreatePayload = z.object({
  email: Email,
  full_name: NonEmptyString,
  role: Role,
  can_validate: z.boolean().default(false),
  can_close_cash: z.boolean().default(false),
  cedula: ShortText.nullable().default(null),
  default_branch_id: Uuid.nullable().default(null),
  password: z.string().min(10).max(256),
});
export type UserCreatePayload = z.infer<typeof UserCreatePayload>;

/** A patch: omitted fields keep their value (`partialWithoutDefaults`, so can_validate is not reset to false by a name change). */
export const UserUpdatePayload = partialWithoutDefaults(UserCreatePayload.omit({ password: true, email: true })).extend({
  user_id: Uuid,
  is_active: z.boolean().optional(),
});
export type UserUpdatePayload = z.infer<typeof UserUpdatePayload>;

// ---- Who signs a branch's reports (Administrador; migration 0013) -----------------------

/**
 * `branch.report_signer.set`. `responsible` needs `responsible_user_id`, an active user who can
 * validate; with `validator` the named user may stay (or be null) and is not printed.
 */
export const BranchReportSignerSetPayload = z
  .object({
    branch_id: Uuid,
    report_signer: ReportSigner,
    responsible_user_id: Uuid.nullable(),
  })
  .refine((p) => p.report_signer === "validator" || p.responsible_user_id !== null, {
    message: "Elige quién es la persona responsable sanitaria.",
    path: ["responsible_user_id"],
  });
export type BranchReportSignerSetPayload = z.infer<typeof BranchReportSignerSetPayload>;

/**
 * `branch.price_list.set` (Administrador, Catálogo → Listas de precios; migration 0018): which list
 * prices new orders and quotes at this branch. `null` returns the branch to the tenant's default
 * list. Existing orders keep the list and the prices they were registered with.
 */
export const BranchPriceListSetPayload = z.object({
  branch_id: Uuid,
  price_list_id: Uuid.nullable(),
});
export type BranchPriceListSetPayload = z.infer<typeof BranchPriceListSetPayload>;

// ---- The validator's own signature (NOM-007 4.7 / 5.1.8, ADR 0013 follow-up 2026-11-02) ------

/** Caps shared by the web app (which prepares the image) and the API (which refuses anything else). */
export const SIGNATURE_LIMITS = { maxBytes: 307_200, maxWidth: 1200, maxHeight: 400 } as const;

/**
 * `signature.set`: always the acting user's own signature — there is no user id to name. A PNG
 * (black ink on a transparent or white background) as base64, or `null` to clear it.
 */
export const SignatureSetPayload = z.object({
  png_base64: z
    .string()
    .min(8)
    .max(Math.ceil(SIGNATURE_LIMITS.maxBytes / 3) * 4)
    .regex(/^[A-Za-z0-9+/]+={0,2}$/, "Imagen inválida.")
    .nullable(),
});
export type SignatureSetPayload = z.infer<typeof SignatureSetPayload>;

/** Metadata of a stored signature; the bytes travel only in `GET /v1/me/signature`. */
export const SignatureInfo = z.object({
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  byte_size: z.number().int().positive(),
  sha256_hex: z.string().regex(/^[0-9a-f]{64}$/),
  updated_at: IsoDateTime,
});
export type SignatureInfo = z.infer<typeof SignatureInfo>;

export const SignatureSetResult = z.object({ signature: SignatureInfo.nullable() });
export type SignatureSetResult = z.infer<typeof SignatureSetResult>;

/** GET /v1/me/signature — the session user's current signature, or null. */
export const MySignature = z.object({ signature: SignatureInfo.extend({ png_base64: z.string() }).nullable() });
export type MySignature = z.infer<typeof MySignature>;

// ---- Activity log (Administrador): the audit chain, read back ----------------------------

export const ActivityQuery = z.object({
  cursor: z.string().max(512).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  user_id: Uuid.optional(),
  entity_type: z.string().trim().max(60).optional(),
  entity_id: Uuid.optional(),
  /** Prefix match on the action ("payment.", "results.validate"). */
  action: z.string().trim().max(80).optional(),
});
export type ActivityQuery = z.infer<typeof ActivityQuery>;

/** One chained event. Payloads are identifiers and hashes only, never values (ADR 0011). */
export const ActivityEvent = z.object({
  seq: z.number().int(),
  action: z.string(),
  entity_type: z.string(),
  entity_id: Uuid.nullable(),
  actor_user_id: Uuid.nullable(),
  /** `operator` marks liveratti support staff (ADR 0017); the laboratory sees them as such. */
  actor_kind: z.enum(["user", "operator"]),
  actor_name: z.string().nullable(),
  device_id: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()),
  occurred_at: IsoDateTime,
  received_at: IsoDateTime,
});
export type ActivityEvent = z.infer<typeof ActivityEvent>;
